import type { GeneratHLAddressParams } from "@src/components/DefuseSDK/sdk/hyperunit/types"
import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import {
  getDefuseAssetId,
  getTokenAid,
  isBaseToken,
} from "@src/components/DefuseSDK/utils/token"
import { getUnderlyingBaseTokenInfos } from "@src/components/DefuseSDK/utils/tokenUtils"

export const HYPERLIQUID_USDC_DEFUSE_ASSET_ID =
  "1cs_v1:hypercore:erc20:0xb88339CB7199b77E23DB6E890353E22632Ba630f"

const HYPERLIQUID_USDC_DEFUSE_ASSET_ID_LOWERCASE =
  HYPERLIQUID_USDC_DEFUSE_ASSET_ID.toLowerCase()

export function isDirectHyperliquidToken(token: TokenInfo): boolean {
  // Only a single base token can be a direct Hyperliquid deployment. Guarding
  // against unified tokens keeps this order-independent: `getDefuseAssetId` on a
  // unified token returns an arbitrary grouped member, which would otherwise
  // flip this result based on token ordering.
  return (
    isBaseToken(token) &&
    getDefuseAssetId(token).startsWith("1cs_v1:hypercore:")
  )
}

export function isDirectHyperliquidUsdcToken(token: TokenInfo): boolean {
  return (
    isBaseToken(token) &&
    getDefuseAssetId(token).toLowerCase() ===
      HYPERLIQUID_USDC_DEFUSE_ASSET_ID_LOWERCASE
  )
}

export function findDirectHyperliquidUsdcToken(
  tokenList: TokenInfo[]
): BaseTokenInfo | undefined {
  return tokenList
    .flatMap((token) => getUnderlyingBaseTokenInfos(token))
    .find(isDirectHyperliquidUsdcToken)
}

export function getHyperliquidSrcChain(
  token: TokenInfo
): GeneratHLAddressParams["srcChain"] {
  const tokenAid = getTokenAid(token)
  switch (tokenAid) {
    case "btc":
      return "bitcoin"
    case "sol":
      return "solana"
    case "eth":
      return "ethereum"
    default:
      throw new Error("Error getting src chain for Hyperliquid")
  }
}

export function getHyperliquidAsset(
  token: TokenInfo
): GeneratHLAddressParams["asset"] {
  const tokenAid = getTokenAid(token)
  switch (tokenAid) {
    case "btc":
      return "btc"
    case "sol":
      return "sol"
    case "eth":
      return "eth"
    default:
      throw new Error("Error getting asset for Hyperliquid")
  }
}

export function isHyperliquid(
  blockchain: SupportedChainName | "near_intents"
): boolean {
  return blockchain === "hyperliquid"
}
